// TS-Spiegel der Design-Tokens (DESIGN §2, §7.1) für JS, Tests und Recharts.
// Quelle der Werte bleibt das CSS (tokens.colors.css, tokens.theme.css); der Test
// tokens/tokens.test.ts prüft, dass die Namen hier und dort gleich sind.

/** Farbrollen (`bg-<name>`, `text-<name>` …), aus `@theme inline` in tokens.theme.css. */
export const COLOR_NAMES = [
  'page',
  'card',
  'card-hover',
  'stripe-subtle',
  'stripe',
  'stripe-hover',
  'tile',
  'tile-hover',
  'tile-selected',
  'selection',
  'selection-hover',
  'accent-soft',
  'tab-sub-active',
  'overlay',
  'input',
  'stage',
  'skeleton',
  'skeleton-shine',
  'pill-page',
  'pill-page-hover',
  'pill-card',
  'pill-card-hover',
  'pill-stripe',
  'pill-stripe-hover',
  'pill-tile',
  'pill-tile-hover',
  'field',
  'field-hover',
  'segment-track',
  'segment-active',
  'primary',
  'primary-hover',
  'on-primary',
  'line',
  'line-subtle',
  'progress-track',
  'ring-track',
  'icon-circle',
  'chip-bg',
  'chip-fg',
  'badge',
  'badge-fg',
  'fg',
  'fg-secondary',
  'fg-tertiary',
  'fg-nav',
  'fg-placeholder',
  'fg-decor',
  'fg-disabled',
  'accent',
  'accent-hover',
  'on-accent',
  'accent-fg',
  'accent-strong',
  'on-accent-strong',
  'brand-from',
  'brand-to',
  'announce-from',
  'announce-to',
  'tile-blue-from',
  'tile-blue-to',
  'tile-purple-from',
  'tile-purple-to',
  'ring-alert',
  'ai',
  'ai-fg',
  'ai-soft',
  'admin',
  'on-admin',
  'success',
  'success-fg',
  'success-soft',
  'warning',
  'warning-fg',
  'warning-soft',
  'danger',
  'danger-fg',
  'danger-soft',
  'danger-solid',
  'on-danger-solid',
  'notify',
  'on-notify',
  'info',
  'info-soft',
  'grade-a',
  'grade-a-fg',
  'grade-a-soft',
  'on-grade-a',
  'grade-above',
  'grade-above-fg',
  'grade-above-soft',
  'on-grade-above',
  'grade-under',
  'grade-under-fg',
  'grade-under-soft',
  'on-grade-under',
  'grade-none',
  'level-poor',
  'level-basic',
  'level-intermediate',
  'level-advanced',
  'level-exceptional',
  'on-level-poor',
  'on-level-basic',
  'on-level-intermediate',
  'on-level-advanced',
  'on-level-exceptional',
  'level-basic-line',
  'chart-1',
  'chart-2',
  'chart-3',
  'chart-4',
  'chart-5',
  'chart-6',
  'chart-7',
  'chart-8',
  'chart-seq-1',
  'chart-seq-2',
  'chart-seq-3',
  'chart-seq-4',
  'chart-seq-5',
  'chart-seq-6',
  'chart-grid',
  'chart-axis',
  'chart-bar-line',
  'focus',
  'bubble',
  'composer',
  'composer-in-card',
  'mention-bg',
  'mention-row',
  'highlight',
  'presence-online',
  'presence-away',
  'presence-offline',
  'unread',
  'on-unread',
  'code',
  'scrim',
  'target-band',
  'avatar-1-from',
  'avatar-1-to',
  'avatar-2-from',
  'avatar-2-to',
  'avatar-3-from',
  'avatar-3-to',
  'avatar-4-from',
  'avatar-4-to',
  'avatar-5-from',
  'avatar-5-to',
  'avatar-6-from',
  'avatar-6-to',
  'avatar-7-from',
  'avatar-7-to',
  'avatar-8-from',
  'avatar-8-to',
  'avatar-ink',
  'on-brand',
] as const;
export type ColorName = (typeof COLOR_NAMES)[number];

/** Typo-Rollen (`text-<rolle>`, DESIGN §2.4). */
export const TEXT_ROLES = [
  'kpi-xl',
  'display',
  'kpi-l',
  'title-page',
  'title-page-m',
  'title-profile',
  'value-section',
  'value-grade',
  'title-detail',
  'kpi',
  'counter',
  'heading',
  'label-strong',
  'body',
  'prose',
  'meta',
  'button',
  'label',
  'small',
  'chip',
  'nav',
  'mono',
] as const;
export type TextRole = (typeof TEXT_ROLES)[number];

/** CSS-Variable einer Farbrolle, z. B. für Recharts (`fill={cssVar('chart-1')}`). */
export const cssVar = (name: ColorName): string => `var(--bar-${name})`;

/** Dauern in ms (DESIGN §2.8, N6); reduzierte Bewegung setzt die CSS-Variablen herab. */
export const DURATION = { instant: 120, fast: 160, base: 220, slow: 260, count: 240 } as const;

/** Die eine Bewegungskurve (N6: ease-out), als Bézier-Punkte für motion und als CSS-Wert. */
export const EASE = [0.2, 0, 0, 1] as const;
export const EASE_CSS = 'cubic-bezier(0.2, 0, 0, 1)';

/** Breakpoints in px (DESIGN §2.6). */
export const BREAKPOINTS = { sm: 480, md: 768, lg: 1024, xl: 1280, '2xl': 1600 } as const;
export type Breakpoint = keyof typeof BREAKPOINTS;

/** Z-Ebenen (DESIGN §2.9). */
export const Z = {
  base: 0,
  sticky: 10,
  nav: 20,
  dock: 30,
  popover: 40,
  sheet: 50,
  dialog: 60,
  toast: 70,
  tooltip: 80,
  skip: 90,
} as const;

/** Layout-Maße in px (DESIGN §2.5). */
export const SIZES = {
  rail: 88,
  topbar: 68,
  topbarMobile: 56,
  bottomnav: 64,
  adminNav: 248,
  aiSidebar: 280,
  aiSidebarCollapsed: 64,
  aiRead: 760,
  aiWide: 1200,
  pageMax: 1440,
  colRight: 343,
  dock: 400,
} as const;

/** Kategorische Diagrammfarben in fester Reihenfolge (DESIGN §3.16). */
export const CHART_SERIES: readonly ColorName[] = [
  'chart-1',
  'chart-2',
  'chart-3',
  'chart-4',
  'chart-5',
  'chart-6',
  'chart-7',
  'chart-8',
];
